/**
 * Stores hooks — TanStack Query wrappers around `StoresApi` and the
 * household-side discover/import endpoints.
 *
 * `useStores()` is the household's imported store list (local rows);
 * `useDiscoverNearbyStores` + `useImportStores` drive the geo-discovery flow.
 * Store selection (which of the imported stores drives offer filtering
 * per invariant I4) is handled by `useSelectedStores` / `useSetSelectedStores`
 * in `useHouseholds.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ImportStoresRequest, StoresResponse } from '../../types';
import type { UpdateStoreRequest } from '../stores';
import { useApi } from './context';
import { keys } from './keys';
import { makeOptimisticCacheUpdate } from './optimistic';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useStores() {
  const api = useApi();
  return useQuery({
    queryKey: keys.stores.all,
    queryFn: () => api.stores.fetchAll(),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function invalidateStores(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.stores.all });
}

export function useUpdateStore() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      storeId,
      input,
    }: {
      storeId: string;
      input: UpdateStoreRequest;
    }) => api.stores.update(storeId, input),
    onSuccess: () => invalidateStores(qc),
  });
}

export function useDeleteStore() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeId: string) => api.stores.delete(storeId),
    onSuccess: () => {
      invalidateStores(qc);
      // Removing a store can unselect it, so refresh selected-stores too.
      void qc.invalidateQueries({ queryKey: keys.households.selectedStores });
    },
  });
}

export function useToggleStoreFavorite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeId: string) => api.stores.toggleFavorite(storeId),
    // BUG21 — flip the matching store's `is_favorite` in the stores cache so
    // the ★ reflects immediately; `onSettled` reconciles. A clean server toggle
    // (net-zero on even taps), so no compose care is needed. The StoresCard
    // favourite no longer carries a `loading` spinner that masked this flip.
    ...makeOptimisticCacheUpdate<StoresResponse, string>({
      queryClient: qc,
      queryKey: keys.stores.all,
      updater: (old, storeId) => {
        if (!old) return old;
        return {
          stores: old.stores.map((s) =>
            s.id === storeId ? { ...s, is_favorite: !s.is_favorite } : s
          ),
        };
      },
    }),
  });
}

// ---------------------------------------------------------------------------
// Discover / search / import (backed by `/api/household/*-stores`)
// ---------------------------------------------------------------------------

export function useDiscoverNearbyStores() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: {
      latitude: number;
      longitude: number;
      radiusKm?: number;
    }) => api.households.discoverNearbyStores(input),
  });
}

// BUG13 D3 — save the user-confirmed checked set from the Stores card
// checklist. Backend finds-or-creates the shared `storelocations` rows AND
// appends entries to `households.store_preferences.selected[]` in one
// transaction. Stores already in the selection are skipped on the prefs
// side, so re-saving is safe.
//
// On success, both the stores list (`keys.stores.all`) and the selected-ids
// list (`keys.households.selectedStores`) are invalidated so the Stores card
// re-renders with the newly saved selection visible immediately.
export function useAddStoresToSelection() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportStoresRequest) => api.households.importStores(input),
    onSuccess: () => {
      invalidateStores(qc);
      void qc.invalidateQueries({ queryKey: keys.households.selectedStores });
    },
  });
}

/**
 * @deprecated BUG13 — renamed to `useAddStoresToSelection` to reflect the new
 * semantics (the save endpoint now appends to `store_preferences` AND
 * resolves the shared store row, not just an `INSERT INTO storelocations`).
 * Kept as an alias for any out-of-tree consumer; remove after one release.
 */
export const useImportStores = useAddStoresToSelection;
